// frontend/src/farm/pathfinding.mjs
// A* over the cell grid, 8-connected without corner cutting. Grids are ≤ 24×24 so a plain
// array open list is fine. Pure (node --test).
import { inFarm, cellKey } from './iso.mjs';

const DIRS = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]];

export function neighbours(cell, size, blocked = () => false) {
  const out = [];
  for (const [dx, dy] of DIRS) {
    const n = { x: cell.x + dx, y: cell.y + dy };
    if (!inFarm(n, size) || blocked(n.x, n.y)) continue;
    // Diagonal only if both orthogonal cells are free (no squeezing between obstacles).
    if (dx !== 0 && dy !== 0 && (blocked(cell.x + dx, cell.y) || blocked(cell.x, cell.y + dy))) continue;
    out.push(n);
  }
  return out;
}

const octile = (a, b) => {
  const dx = Math.abs(a.x - b.x), dy = Math.abs(a.y - b.y);
  return Math.max(dx, dy) + (Math.SQRT2 - 1) * Math.min(dx, dy);
};

export function findPath(from, to, size, blocked = () => false, { allowBlockedGoal = false } = {}) {
  if (!inFarm(from, size) || !inFarm(to, size)) return null;
  if (from.x === to.x && from.y === to.y) return [];
  if (blocked(to.x, to.y) && !allowBlockedGoal) return null;
  const goalKey = cellKey(to.x, to.y);
  const isBlocked = (x, y) => (allowBlockedGoal && x === to.x && y === to.y) ? false : blocked(x, y);
  const g = new Map([[cellKey(from.x, from.y), 0]]);
  const parent = new Map();
  const open = [{ cell: from, f: octile(from, to) }];
  const closed = new Set();
  while (open.length) {
    let bi = 0;
    for (let i = 1; i < open.length; i++) if (open[i].f < open[bi].f) bi = i;
    const { cell } = open.splice(bi, 1)[0];
    const key = cellKey(cell.x, cell.y);
    if (key === goalKey) {
      const path = [];
      let k = key;
      while (k !== cellKey(from.x, from.y)) { const [x, y] = k.split(',').map(Number); path.push({ x, y }); k = parent.get(k); }
      return path.reverse();
    }
    if (closed.has(key)) continue;
    closed.add(key);
    for (const n of neighbours(cell, size, isBlocked)) {
      const nk = cellKey(n.x, n.y);
      if (closed.has(nk)) continue;
      const step = (n.x !== cell.x && n.y !== cell.y) ? Math.SQRT2 : 1;
      const cand = g.get(key) + step;
      if (cand < (g.get(nk) ?? Infinity)) {
        g.set(nk, cand);
        parent.set(nk, key);
        open.push({ cell: n, f: cand + octile(n, to) });
      }
    }
  }
  return null;
}
